import HelpCenterCard from '@/components/HelpCenterCard';
import SEOHead from '@/components/SEOHead';
import SocialShare from '@/components/SocialShare';
import { client } from '@/utils/sanity-client';
import { SearchOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Card, Col, Empty, Input, Row, Skeleton, Space, Typography } from 'antd';
import { useRouter } from 'next/router';
import { useState } from 'react';

const { Title, Text } = Typography;

// Add this mapping object at the top level
const urlToTypeMap = {
  'case-studies': 'caseStudies',
  'industry-news': 'industryNews',
  articles: 'articles',
  faq: 'faq',
  guides: 'guides',
};

const typeToUrlMap = {
  caseStudies: 'case-studies',
  industryNews: 'industry-news',
  articles: 'articles',
  faq: 'faq',
  guides: 'guides',
};

// Get static paths for all possible categories
export async function getStaticPaths() {
  const paths = [
    { params: { category: 'articles' } },
    { params: { category: 'faq' } },
    { params: { category: 'case-studies' } },
    { params: { category: 'guides' } },
    { params: { category: 'industry-news' } },
  ];
  return { paths, fallback: false };
}

// Get static props for each category
export async function getStaticProps({ params }) {
  const { category } = params;
  const type = urlToTypeMap[category] || category; // Map URL to Sanity type

  try {
    const documents = await client.fetch(
      `*[_type == $type] | order(title asc) {
        _id,
        title,
        slug,
        description,
        _type,
        question,
        answer,
        body,
        publishedAt
      }`,
      { type }
    );

    return {
      props: {
        documents: documents || [],
        category: type, // Pass the Sanity type for proper component rendering
        urlCategory: category, // Pass the URL-friendly category for links
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error('Error fetching category data:', error);
    return {
      props: {
        documents: [],
        category: type,
        urlCategory: category,
        error: 'Failed to load content',
      },
    };
  }
}

const CategoryPage = ({ documents: initialDocuments, category, urlCategory, error }) => {
  const [searchText, setSearchText] = useState('');
  const router = useRouter();

  // Show loading state during fallback
  if (router.isFallback) {
    return (
      <div style={{ minHeight: 'calc(100vh - 100px)' }}>
        <section className="section hero v4" style={{ paddingTop: '132px', paddingBottom: '0' }}>
          <div className="container-default w-container">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </section>
        <div className="overflow-hidden" style={{ minHeight: 'calc(100vh - 300px)' }}>
          <section className="section pd-top-0px bg-neutral-100">
            <div className="container-default w-container">
              <Row gutter={[24, 24]}>
                {[...Array(6)].map((_, i) => (
                  <Col xs={24} key={i}>
                    <Card>
                      <Skeleton active />
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const formatCategory = (category) => {
    return category
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  const filteredDocuments = initialDocuments.filter((doc) => {
    const searchContent = [doc.title, doc.question, doc.name, doc.preview, doc.definition]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchContent.includes(searchText.toLowerCase());
  });

  const formattedCategory = formatCategory(category);
  const fullUrl = `https://brandymail.com/${urlCategory}`;
  const title = `Brandymail.com - ${formattedCategory}`;

  // Show error state if there was an error fetching data
  if (error) {
    return (
      <div style={{ minHeight: 'calc(100vh - 100px)', padding: '40px' }}>
        <Empty description={error} style={{ margin: '48px 0' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: 'calc(100vh - 100px)' }}>
      <SEOHead />

      <section className="section hero v4" style={{ paddingTop: '132px', paddingBottom: '0' }}>
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        Brandymail™ Help Center
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">{formattedCategory}</h1>
                    <Text className="color-neutral-600">
                      {formattedCategory === 'Articles' &&
                        'Discover in-depth guides and expert insights about email branding and BIMI implementation'}
                      {formattedCategory === 'FAQs' &&
                        'Find quick answers to common questions about email branding and BIMI'}
                      {formattedCategory === 'Case Studies' &&
                        'Learn from real-world success stories and implementation examples'}
                      {formattedCategory === 'Industry News' &&
                        'Stay updated with the latest developments in email branding and security'}
                      {formattedCategory === 'Guides' &&
                        'Follow step-by-step tutorials and comprehensive implementation guides'}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="position-absolute card-top-bg---bottom pricing-bg" />
      </section>

      <div className="overflow-hidden" style={{ minHeight: 'calc(100vh - 300px)' }}>
        <section className="section pd-top-0px bg-neutral-100 pd-0px">
          <div
            className="container-default w-container inner-container center"
            style={{ paddingBottom: '120px' }}
          >
            <Row gutter={[48, 48]}>
              <Col xs={24} md={16}>
                <div className="mg-bottom-32px">
                  <Input
                    size="large"
                    placeholder={`Search ${formattedCategory.toLowerCase()}...`}
                    prefix={<SearchOutlined />}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                </div>

                {filteredDocuments.length > 0 ? (
                  <Row gutter={[24, 24]}>
                    {filteredDocuments.map((doc) => (
                      <Col xs={24} key={doc._id}>
                        <HelpCenterCard item={doc} type={category} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty
                    description={`No ${formattedCategory.toLowerCase()} found`}
                    style={{ margin: '48px 0' }}
                  />
                )}
              </Col>

              <Col xs={24} md={8}>
                <div
                  style={{
                    position: 'sticky',
                    top: '100px',
                    height: 'fit-content',
                    marginBottom: '40px',
                  }}
                >
                  <Card
                    className="share-card"
                    style={{
                      borderRadius: '12px',
                      background: '#ffffff',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Title level={5} style={{ margin: '0 0 12px 0' }}>
                        <ShareAltOutlined style={{ marginRight: '8px' }} /> Share this{' '}
                        {formattedCategory} page
                      </Title>
                      <SocialShare url={fullUrl} title={title} />
                    </Space>
                  </Card>
                </div>
              </Col>
            </Row>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CategoryPage;
